import { useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FiArrowLeft, FiCode, FiFileText, FiMail } from "react-icons/fi";

import AdminLayout from "@/layouts/AdminLayout";
import Breadcrumbs from "@/components/shared/nav/Breadcrumbs";
import { useEmailTemplates } from "@/api/features/email-templates";

export default function ViewEmailTemplateDetail() {
  const navigate = useNavigate();
  const { key } = useParams<{ key: string }>();
  const { data, isLoading, isError, error, refetch } = useEmailTemplates();

  const decodedKey = decodeURIComponent(key ?? "");
  const item = (data?.items ?? []).find((x) => x.key === decodedKey) ?? null;
  const renderedPreviewHtml = useMemo(
    () => extractEmailBodyHtml(item?.previewHtml ?? ""),
    [item?.previewHtml]
  );

  return (
    <AdminLayout>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">
            {item?.name || "Email Template"}
          </h1>
          <div className="mt-1">
            <Breadcrumbs />
          </div>
          {item && <p className="mt-2 text-sm text-gray-600">{item.description}</p>}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/email-templates")}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <FiArrowLeft />
            Back to List
          </button>
        </div>
      </div>

      {isLoading && (
        <div className="rounded-2xl border border-gray-200 bg-white px-4 py-8 text-sm text-gray-600 shadow-sm">
          Loading email template…
        </div>
      )}

      {isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          Failed to load email template: {(error as Error)?.message || "Unknown error"}
          <button
            type="button"
            onClick={() => refetch()}
            className="ml-3 rounded-md border border-red-200 bg-white px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50"
          >
            Retry
          </button>
        </div>
      )}

      {!isLoading && !isError && !item && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          Template not found. <Link to="/email-templates" className="underline">Go back to list</Link>
        </div>
      )}

      {!isLoading && !isError && item && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <InfoCard label="Category" value={item.category} />
            <InfoCard label="Status" value={item.status} />
            <InfoCard label="Subject" value={item.subject} />
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900">
              <FiMail className="text-sky-600" />
              Rendered Email
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-0 overflow-hidden">
              <div
                className="min-h-[700px] overflow-auto bg-white"
                dangerouslySetInnerHTML={{ __html: renderedPreviewHtml }}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900">
                <FiFileText className="text-sky-600" />
                Plain Text Version
              </div>
              <pre className="max-h-[420px] overflow-auto whitespace-pre-wrap rounded-xl border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700">
                {item.previewText}
              </pre>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900">
                <FiCode className="text-sky-600" />
                HTML Source
              </div>
              <pre className="max-h-[420px] overflow-auto whitespace-pre-wrap rounded-xl border border-gray-200 bg-gray-50 p-3 text-[11px] text-gray-700">
                {item.previewHtml}
              </pre>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function InfoCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-2 text-sm font-medium text-gray-900">{value}</div>
    </div>
  );
}

function extractEmailBodyHtml(html: string): string {
  const trimmed = html.trim();
  if (!trimmed) return '<div style="padding:24px;font-family:system-ui,sans-serif;color:#6b7280;">No preview available.</div>';

  const bodyMatch = trimmed.match(/<body[^>]*>([\s\S]*)<\/body>/i);
  return bodyMatch?.[1]?.trim() || trimmed;
}
